import Menu from './menu';

import { Opcodes, Packets } from '@kaetram/common/network';

import type Game from '../game';
import type {
    CTFCampaignNode,
    CTFCampaignState,
    CTFChallengePacket
} from '@kaetram/common/types/messages/outgoing';

const CATEGORY_LABELS: { [k: string]: string } = {
        web: 'Web',
        api: 'API',
        reversing: 'Reversing',
        crypto: 'Crypto',
        forensics: 'Forensics',
        pwn: 'Pwn',
        misc: 'Misc'
    },
    DIFFICULTY_LABELS: { [k: string]: string } = {
        easy: 'Fácil',
        medium: 'Medio',
        hard: 'Difícil',
        insane: 'Insano'
    };

/**
 * CTF campaign menu. Opened by a server packet (CTFChallenge.Open) when the
 * player talks to the hackl4bs NPC — no HUD button. Renders the active node
 * of the campaign with type-specific UI:
 *  - ctf / boss: title + description + category/difficulty badges + flag input
 *  - kill:       mob name + progress bar
 *  - treasure:   coords + "found it" button
 * Past nodes show as ✓ in the sidebar list; future nodes stay locked.
 */

export default class CTFChallenges extends Menu {
    private active: HTMLElement = document.querySelector('#ctfchallenges-active')!;
    private list: HTMLUListElement = document.querySelector('#ctfchallenges-list')!;
    private msg: HTMLElement = document.querySelector('#ctfchallenges-msg')!;

    // Latest state received from server; kept so `handleKillProgress` can patch
    // it in-place without a re-fetch.
    private state?: CTFCampaignState;
    private questionTimer?: ReturnType<typeof setInterval>;

    public constructor(private game: Game) {
        // No toggle button — this menu is opened by a server packet.
        super('#ctfchallenges', '#close-ctfchallenges');
    }

    /**
     * Called by connection.handleCTFChallenge when the server sends the Open
     * opcode (i.e. player just talked to hackl4bs). Shows the menu and asks
     * for the current campaign state.
     */
    public openFromServer(): void {
        this.show();
        this.active.innerHTML = 'Cargando misión...';
        this.list.innerHTML = '';
        this.hideMsg();

        this.game.socket.send(Packets.CTFChallenge, {
            opcode: Opcodes.CTFChallenge.Request
        });
    }

    /**
     * Renders the full campaign state received from the server. The server
     * only hydrates the active node with MMOCTF metadata — past/future nodes
     * arrive with just id/type/status.
     */
    public renderState(state: CTFCampaignState): void {
        this.clearQuestionTimer();
        this.state = state;
        this.active.innerHTML = '';
        this.list.innerHTML = '';

        // Campaign fully completed.
        if (state.currentIndex === -1) {
            this.active.innerHTML = `
                <div class="ctf-empty">
                    <div class="ctf-empty-title stroke">¡Has completado la campaña CTF!</div>
                    <div class="ctf-empty-sub">Espera a que HackL4bs publique nuevos retos.</div>
                </div>`;
            this.renderNodeList(state);
            return;
        }

        let activeNode = state.nodes[state.currentIndex];
        this.active.append(this.renderActiveNode(activeNode));
        this.renderNodeList(state);
    }

    /**
     * Incremental kill counter update — much cheaper than a full re-render.
     * Only patches the killed number in the active kill/boss node.
     */
    public handleKillProgress(info: CTFChallengePacket): void {
        if (!this.state || typeof info.killed !== 'number') return;

        let node = this.state.nodes[this.state.currentIndex];
        if (!node || (node.type !== 'kill' && node.type !== 'boss')) return;

        node.killed = info.killed;
        if (typeof info.required === 'number') node.required = info.required;

        // Update just the progress line if the DOM was rendered.
        let killedEl = this.active.querySelector('.ctf-kill-killed');
        if (killedEl) killedEl.textContent = `${node.killed}`;

        let barEl = this.active.querySelector<HTMLElement>('.ctf-kill-bar-fill');
        if (barEl && node.required)
            barEl.style.width = `${Math.min(100, (node.killed / node.required) * 100)}%`;
    }

    /**
     * Shows a transient feedback message after a submit (correct or incorrect).
     */
    public showResult(info: CTFChallengePacket): void {
        this.msg.classList.remove('ctf-msg-ok', 'ctf-msg-err');
        this.msg.classList.add(info.correct ? 'ctf-msg-ok' : 'ctf-msg-err');
        this.msg.textContent = info.message ?? '';
        this.msg.hidden = false;

        // Auto-hide after 5s for non-blocking UX.
        setTimeout(() => this.hideMsg(), 5000);
    }

    private hideMsg(): void {
        this.msg.hidden = true;
        this.msg.textContent = '';
    }

    // ---------- Active node renderer -----------------------------------------

    private renderActiveNode(node: CTFCampaignNode): HTMLElement {
        let wrapper = document.createElement('div');
        wrapper.classList.add('ctf-active-node', `ctf-type-${node.type}`);

        let underLeveled = this.isUnderLeveled(node);
        if (underLeveled) wrapper.classList.add('ctf-under-leveled');

        // Common header: type + title.
        let header = document.createElement('div');
        header.classList.add('ctf-node-header');

        let typeBadge = document.createElement('span');
        typeBadge.classList.add('ctf-type-badge');
        typeBadge.textContent = this.typeLabel(node);
        header.append(typeBadge);

        let title = document.createElement('div');
        title.classList.add('ctf-node-title', 'stroke');
        title.textContent = node.title ?? this.defaultTitle(node);
        header.append(title);

        wrapper.append(header);

        // Level gate banner — shown ABOVE the type-specific body when the
        // player is under the required level. The interactive controls below
        // are also disabled by isUnderLeveled().
        if (underLeveled) wrapper.append(this.renderLevelBanner(node));

        // Type-specific body.
        switch (node.type) {
            case 'ctf': {
                wrapper.append(this.renderCtfBody(node));
                break;
            }
            case 'kill': {
                wrapper.append(this.renderKillBody(node));
                break;
            }
            case 'treasure': {
                wrapper.append(this.renderTreasureBody(node));
                break;
            }
            case 'question': {
                wrapper.append(this.renderQuestionBody(node));
                break;
            }
            case 'boss': {
                wrapper.append(this.renderKillBody(node));
                wrapper.append(this.renderCtfBody(node, true));
                break;
            }
        }

        return wrapper;
    }

    /**
     * True when the node has a minLevel and the player is below it.
     */
    private isUnderLeveled(node: CTFCampaignNode): boolean {
        if (!node.minLevel) return false;
        if (typeof node.playerLevel !== 'number') return false;
        return node.playerLevel < node.minLevel;
    }

    private renderLevelBanner(node: CTFCampaignNode): HTMLElement {
        let banner = document.createElement('div');
        banner.classList.add('ctf-level-banner');
        banner.innerHTML = `
            <span class="ctf-level-icon">⚠️</span>
            <span class="ctf-level-text">
                Requiere <strong>Nv. ${node.minLevel}</strong>
                &middot; Tu nivel:
                <strong>${node.playerLevel ?? '?'}</strong>
            </span>
        `;
        return banner;
    }

    private renderCtfBody(node: CTFCampaignNode, isBoss = false): HTMLElement {
        let body = document.createElement('div');
        body.classList.add('ctf-node-body');

        let disabled = this.isUnderLeveled(node);

        // Description + metadata badges (category, difficulty, points).
        // The description often embeds a string to work with (Base64, hex, a
        // ciphertext). We render backtick-delimited spans as copyable code
        // blocks so the player doesn't have to transcribe them by hand.
        if (node.description) body.append(this.renderDescription(node.description));

        let meta = document.createElement('div');
        meta.classList.add('ctf-node-meta');

        if (node.category) {
            let cat = document.createElement('span');
            cat.classList.add('ctf-badge', `ctf-cat-${node.category}`);
            cat.textContent = CATEGORY_LABELS[node.category] ?? node.category;
            meta.append(cat);
        }
        if (node.difficulty) {
            let diff = document.createElement('span');
            diff.classList.add('ctf-badge', `ctf-diff-${node.difficulty}`);
            diff.textContent = DIFFICULTY_LABELS[node.difficulty] ?? node.difficulty;
            meta.append(diff);
        }
        if (typeof node.points === 'number') {
            let pts = document.createElement('span');
            pts.classList.add('ctf-badge', 'ctf-badge-points');
            pts.textContent = `${node.points} pts`;
            meta.append(pts);
        }
        if (typeof node.rewardCoins === 'number') {
            let rw = document.createElement('span');
            rw.classList.add('ctf-badge', 'ctf-badge-reward');
            rw.textContent = `+${node.rewardCoins} CoinCTF`;
            meta.append(rw);
        }
        body.append(meta);

        // "Abrir reto" / "Descargar archivo" — gated behind the level check.
        // If the player is under-leveled we DON'T render these at all, so the
        // challenge material (hosted URL / file) stays locked until they meet
        // the requirement — same gate as the flag input below.
        if (!disabled && node.hosted_url) {
            let openBtn = document.createElement('button');
            openBtn.classList.add('ctf-btn', 'ctf-btn-open');
            openBtn.textContent = '🌐 Abrir reto en pestaña nueva';
            openBtn.addEventListener('click', () =>
                window.open(node.hosted_url, '_blank', 'noopener')
            );
            body.append(openBtn);
        } else if (!disabled && node.has_file && node.download_url) {
            let dl = document.createElement('a');
            dl.classList.add('ctf-btn', 'ctf-btn-open');
            dl.textContent = '📥 Descargar archivo del reto';
            dl.href = node.download_url;
            dl.target = '_blank';
            dl.rel = 'noopener';
            body.append(dl);
        }

        // Flag submit form (boss ctf-body doesn't repeat the header, but does
        // repeat the flag input so the boss can see both kill progress and flag).
        let formLabel = isBoss ? 'Envía la flag final para vencer al jefe:' : 'Envía tu flag:',
            label = document.createElement('div');
        label.classList.add('ctf-form-label');
        label.textContent = formLabel;
        body.append(label);

        let form = document.createElement('div');
        form.classList.add('ctf-flag-form');

        let input = document.createElement('input');
        input.classList.add('ctf-flag-input');
        input.type = 'text';
        input.placeholder = 'HL4{...}';
        input.autocomplete = 'off';
        input.maxLength = 256;
        input.disabled = disabled;

        let submit = document.createElement('button');
        submit.classList.add('ctf-btn', 'ctf-btn-submit');
        submit.textContent = 'Validar';
        submit.disabled = disabled;

        let doSubmit = (): void => {
            let value = input.value.trim();
            if (!value) return;

            submit.disabled = true;
            this.game.socket.send(Packets.CTFChallenge, {
                opcode: Opcodes.CTFChallenge.Submit,
                flag: value
            });
            // Re-enable soon so if the network drops the user can retry.
            setTimeout(() => (submit.disabled = false), 1500);
        };

        submit.addEventListener('click', doSubmit);
        // Prevent Enter from triggering global game hotkeys (chat, etc.) — same
        // pattern used in quests.ts.
        input.addEventListener('keydown', (e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
                e.preventDefault();
                doSubmit();
            }
        });

        form.append(input, submit);
        body.append(form);

        return body;
    }

    private clearQuestionTimer(): void {
        if (this.questionTimer) {
            clearInterval(this.questionTimer);
            this.questionTimer = undefined;
        }
    }

    private renderQuestionBody(node: CTFCampaignNode): HTMLElement {
        let body = document.createElement('div'),
            disabled = this.isUnderLeveled(node);
        body.classList.add('ctf-question-body');

        let questionEl = document.createElement('div');
        questionEl.classList.add('ctf-question-text', 'stroke');
        questionEl.textContent = node.questionText ?? 'Pregunta no disponible.';
        body.append(questionEl);

        if (node.questionHint) {
            let hint = document.createElement('div');
            hint.classList.add('ctf-question-hint');
            hint.textContent = `Pista: ${node.questionHint}`;
            body.append(hint);
        }

        // Reward badge
        if (typeof node.rewardCoins === 'number') {
            let meta = document.createElement('div');
            meta.classList.add('ctf-meta');
            let rw = document.createElement('span');
            rw.classList.add('ctf-badge', 'ctf-badge-reward');
            rw.textContent = `+${node.rewardCoins} CoinCTF`;
            meta.append(rw);
            body.append(meta);
        }

        // Countdown timer
        let timerWrap = document.createElement('div');
        timerWrap.classList.add('ctf-question-timer');
        let timeLeft = node.questionTimeLeft ?? 0,
            timerText = document.createElement('span');
        timerText.classList.add('ctf-timer-text');
        timerText.textContent = this.formatTime(timeLeft);
        timerWrap.append(timerText);
        body.append(timerWrap);

        // Answer form
        let label = document.createElement('div');
        label.classList.add('ctf-form-label');
        label.textContent = 'Tu respuesta:';
        body.append(label);

        let form = document.createElement('div');
        form.classList.add('ctf-flag-form');

        let input = document.createElement('input');
        input.classList.add('ctf-flag-input');
        input.type = 'text';
        input.placeholder = 'Escribe tu respuesta...';
        input.autocomplete = 'off';
        input.maxLength = 256;
        input.disabled = disabled;

        let submit = document.createElement('button');
        submit.classList.add('ctf-btn', 'ctf-btn-submit');
        submit.textContent = 'Responder';
        submit.disabled = disabled;

        let doSubmit = (): void => {
            let value = input.value.trim();
            if (!value) return;
            submit.disabled = true;
            this.game.socket.send(Packets.CTFChallenge, {
                opcode: Opcodes.CTFChallenge.QuestionAnswer,
                answer: value
            });
            setTimeout(() => (submit.disabled = false), 1500);
        };

        submit.addEventListener('click', doSubmit);
        input.addEventListener('keydown', (e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
                e.preventDefault();
                doSubmit();
            }
        });

        form.append(input, submit);
        body.append(form);

        // Client-side countdown — visual only, server is the authority
        this.clearQuestionTimer();
        if (timeLeft > 0 && !disabled)
            this.questionTimer = setInterval(() => {
                timeLeft--;
                timerText.textContent = this.formatTime(timeLeft);

                if (timeLeft <= 30) timerWrap.classList.add('ctf-timer-urgent');

                if (timeLeft <= 0) {
                    this.clearQuestionTimer();
                    submit.disabled = true;
                    input.disabled = true;
                    timerText.textContent = '00:00 — Tiempo agotado';
                    // Request fresh state from server (new question)
                    this.game.socket.send(Packets.CTFChallenge, {
                        opcode: Opcodes.CTFChallenge.QuestionAnswer,
                        answer: ''
                    });
                }
            }, 1000);

        return body;
    }

    private formatTime(seconds: number): string {
        let m = Math.floor(seconds / 60),
            s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }

    /**
     * Renders a challenge description, turning `backtick-delimited` spans into
     * copyable code blocks (challenges embed Base64/hex/ciphertext the player
     * needs verbatim — transcribing by hand is error-prone).
     */
    private renderDescription(text: string): HTMLElement {
        let desc = document.createElement('div');
        desc.classList.add('ctf-node-desc');

        for (let part of text.split(/(`[^`]+`)/g))
            if (part.length > 2 && part.startsWith('`') && part.endsWith('`'))
                desc.append(this.renderCopyable(part.slice(1, -1)));
            else if (part) desc.append(document.createTextNode(part));

        return desc;
    }

    /**
     * A monospace code chip with a copy-to-clipboard button.
     */
    private renderCopyable(text: string): HTMLElement {
        let wrap = document.createElement('span');
        wrap.classList.add('ctf-code');

        let code = document.createElement('code');
        code.textContent = text;

        let btn = document.createElement('button');
        btn.type = 'button';
        btn.classList.add('ctf-copy-btn');
        btn.textContent = '📋';
        btn.title = 'Copiar';
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.copyToClipboard(text, btn);
        });

        wrap.append(code, btn);
        return wrap;
    }

    private copyToClipboard(text: string, btn: HTMLButtonElement): void {
        let feedback = (): void => {
            btn.textContent = '✅';
            btn.classList.add('ctf-copied');
            setTimeout(() => {
                btn.textContent = '📋';
                btn.classList.remove('ctf-copied');
            }, 1200);
        };

        // navigator.clipboard needs a secure context (localhost counts). Fall
        // back to the legacy execCommand path for plain-HTTP remote hosts.
        if (navigator.clipboard?.writeText)
            navigator.clipboard
                .writeText(text)
                .then(feedback)
                .catch(() => this.fallbackCopy(text, feedback));
        else this.fallbackCopy(text, feedback);
    }

    private fallbackCopy(text: string, feedback: () => void): void {
        let ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.append(ta);
        ta.select();

        try {
            document.execCommand('copy');
            feedback();
        } catch {
            // Clipboard unavailable — leave the text selected so the player can
            // copy manually with Ctrl+C.
        }

        ta.remove();
    }

    private renderKillBody(node: CTFCampaignNode): HTMLElement {
        let body = document.createElement('div');
        body.classList.add('ctf-node-body', 'ctf-kill-body');

        let goal = document.createElement('div');
        goal.classList.add('ctf-node-desc');
        let mobName = node.mobKey ?? '???';
        goal.textContent =
            node.type === 'boss'
                ? `Antes de la flag: derrota a ${mobName}.`
                : `Elimina a ${node.required ?? 1} ${mobName} para desbloquear el siguiente reto.`;
        body.append(goal);

        let progress = document.createElement('div');
        progress.classList.add('ctf-kill-progress');

        let stats = document.createElement('div');
        stats.classList.add('ctf-kill-stats');
        stats.innerHTML = `
            <span class="ctf-kill-killed">${node.killed ?? 0}</span>
            <span class="ctf-kill-sep">/</span>
            <span class="ctf-kill-required">${node.required ?? 1}</span>
        `;
        progress.append(stats);

        let bar = document.createElement('div');
        bar.classList.add('ctf-kill-bar');
        let fill = document.createElement('div');
        fill.classList.add('ctf-kill-bar-fill');
        let pct = Math.min(100, ((node.killed ?? 0) / (node.required ?? 1)) * 100);
        fill.style.width = `${pct}%`;
        bar.append(fill);
        progress.append(bar);

        body.append(progress);
        return body;
    }

    private renderTreasureBody(node: CTFCampaignNode): HTMLElement {
        let body = document.createElement('div');
        body.classList.add('ctf-node-body');

        let desc = document.createElement('div');
        desc.classList.add('ctf-node-desc');
        desc.textContent = `Encuentra el tesoro escondido en las coordenadas (${
            node.chestX ?? '?'
        }, ${node.chestY ?? '?'}).`;
        body.append(desc);

        let disabled = this.isUnderLeveled(node),
            btn = document.createElement('button');
        btn.classList.add('ctf-btn', 'ctf-btn-submit');
        btn.textContent = '🗺️ Estoy en el lugar del tesoro';
        btn.disabled = disabled;
        btn.addEventListener('click', () => {
            btn.disabled = true;
            this.game.socket.send(Packets.CTFChallenge, {
                opcode: Opcodes.CTFChallenge.TreasureFound
            });
            setTimeout(() => (btn.disabled = false), 1500);
        });
        body.append(btn);

        return body;
    }

    // ---------- Sidebar list ------------------------------------------------

    private renderNodeList(state: CTFCampaignState): void {
        for (let i = 0; i < state.nodes.length; i++) {
            let node = state.nodes[i],
                li = document.createElement('li');
            li.classList.add('ctf-list-item', `ctf-list-${node.status}`);

            let icon = document.createElement('span');
            icon.classList.add('ctf-list-icon');
            icon.textContent = this.statusIcon(node);
            li.append(icon);

            let label = document.createElement('span');
            label.classList.add('ctf-list-label');
            label.textContent = `#${i + 1} — ${this.typeLabel(node)}`;
            li.append(label);

            this.list.append(li);
        }
    }

    private typeLabel(node: CTFCampaignNode): string {
        switch (node.type) {
            case 'ctf': {
                return 'Reto CTF';
            }
            case 'kill': {
                return 'Combate';
            }
            case 'treasure': {
                return 'Tesoro';
            }
            case 'question': {
                return 'Pregunta';
            }
            case 'boss': {
                return 'JEFE FINAL';
            }
        }
    }

    private defaultTitle(node: CTFCampaignNode): string {
        switch (node.type) {
            case 'kill': {
                return 'Elimina a los enemigos';
            }
            case 'treasure': {
                return 'Busca el tesoro';
            }
            case 'question': {
                return 'Responde la pregunta';
            }
            case 'boss': {
                return 'Derrota al Jefe';
            }
            default: {
                return 'Reto';
            }
        }
    }

    private statusIcon(node: CTFCampaignNode): string {
        switch (node.status) {
            case 'completed': {
                return '✓';
            }
            case 'active': {
                return '●';
            }
            case 'locked': {
                return '🔒';
            }
        }
    }
}
